import React, { useState, useRef, useEffect, useId } from 'react'

// Collapses children to a fixed pixel height and reveals a "show more" control when
// the content overflows. Full content stays in the DOM (overflow:hidden only hides it
// visually) so search engines can still index it.
function ClampableBlock({ children, translations, collapsedMaxPx = 96 }) {
  const ref = useRef(null)
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  const contentId = useId()

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // scrollHeight reports full content height regardless of the clamp, so this works
    // whether or not maxHeight is currently applied. The +4 absorbs sub-pixel rounding.
    const measure = () => setOverflowing(el.scrollHeight > collapsedMaxPx + 4)
    measure()
    // Re-measure on width changes (viewport resize / rotation); otherwise text that
    // newly wraps past the clamp would be clipped with no way to expand it.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(el)
    return () => ro?.disconnect()
  }, [children, collapsedMaxPx])

  // Only clamp when the content actually overflows; otherwise leave it unconstrained so
  // short content is never clipped (the clamp would otherwise apply even with no toggle).
  const clamped = overflowing && !expanded

  return (
    <>
      <div
        id={contentId}
        ref={ref}
        style={clamped ? { maxHeight: `${collapsedMaxPx}px`, overflow: 'hidden' } : undefined}
      >
        {children}
      </div>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={contentId}
          className="mt-1 text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity"
          style={{ color: '#3366FF' }}
        >
          {expanded ? (translations?.showLess || 'Show less') : (translations?.showMore || 'Show more')}
        </button>
      )}
    </>
  )
}

/**
 * Shared rendering of a teacher's profile fields, used by both the multi-teacher
 * profile modal (`variant="modal"`) and the single-teacher inline section
 * (`variant="inline"`). Keeping the field markup in one place means field changes
 * apply to both modes.
 *
 * The avatar and name are rendered by the parent (their layout differs between modal
 * and inline). This component renders: headline, teaching years, specialties, bio,
 * qualifications, optional consultation CTA — plus a `beforeConsultation` slot the
 * modal uses to inject its group-class list between qualifications and the CTA,
 * preserving the original modal order.
 *
 * Empty fields are omitted entirely (matching the existing modal behavior).
 */
export default function TeacherProfileContent({
  member,
  colors,
  translations,
  variant = 'modal',
  consultationEnabled,
  onConsultClick,
  beforeConsultation = null,
}) {
  const isInline = variant === 'inline'

  return (
    <>
      {/* Headline — one-line intro */}
      {member.headline && (
        <p className={isInline ? '' : 'mb-2'} style={{ color: '#717171' }}>{member.headline}</p>
      )}

      {member.teachingYears && member.teachingYears > 0 && (
        <p className={isInline ? 'text-sm mt-1' : 'text-sm mb-4'} style={{ color: '#717171' }}>
          {member.teachingYears} {translations?.teachingYearsLabel || 'year(s) of teaching experience'}
        </p>
      )}

      {/* Specialties */}
      {member.specialties && member.specialties.length > 0 && (
        <div className={isInline ? 'mt-4' : 'mb-4'}>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">{translations?.specialties || 'Specialties'}</h4>
          <div className={`flex flex-wrap gap-2 ${isInline ? 'justify-center md:justify-start' : 'justify-center'}`}>
            {member.specialties.map((specialty, idx) => (
              <span
                key={idx}
                className="px-3 py-1.5 text-sm font-medium rounded-full"
                style={{ backgroundColor: colors.background, color: colors.primaryDark }}
              >
                {specialty}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Bio */}
      {member.bio && (
        <div className="text-left bg-gray-50 rounded-xl p-4 mt-4">
          {/* In inline mode the section title ("About the Teacher") already covers this, so the heading is omitted to avoid duplication. */}
          {!isInline && (
            <h4 className="text-sm font-semibold text-gray-700 mb-2">{translations?.aboutTeacher || 'About the Teacher'}</h4>
          )}
          {isInline ? (
            <ClampableBlock translations={translations}>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{member.bio}</p>
            </ClampableBlock>
          ) : (
            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{member.bio}</p>
          )}
        </div>
      )}

      {/* Qualifications if available */}
      {member.qualifications && member.qualifications.length > 0 && (
        <div className="text-left bg-gray-50 rounded-xl p-4 mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">{translations?.educationLabel || 'Education & Experience'}</h4>
          {isInline ? (
            <ClampableBlock translations={translations}>
              <ul className="text-gray-600 text-sm space-y-1">
                {member.qualifications.map((qual, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    <span>{qual}</span>
                  </li>
                ))}
              </ul>
            </ClampableBlock>
          ) : (
            <ul className="text-gray-600 text-sm space-y-1">
              {member.qualifications.map((qual, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  <span>{qual}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {beforeConsultation}

      {/* Consultation CTA */}
      {consultationEnabled && onConsultClick && (
        <div className="mt-6 rounded-xl p-4" style={{ backgroundColor: '#E6F0FF' }}>
          <p className="text-sm text-gray-700 mb-2">{translations?.memberConsultTitle || '想了解更多？'}</p>
          <button
            onClick={() => onConsultClick(member)}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium cursor-pointer transition-colors hover:opacity-90"
            style={{ backgroundColor: colors?.primary || '#3366FF' }}
          >
            {translations?.contactTeacher || '聯繫老師'}
          </button>
        </div>
      )}
    </>
  )
}
